import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, FileSignature, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API, errMsg } from './careApi';
import { Btn } from './careUi';
import { Admission, Pdpa, Contract, Checklist, SigBlock } from './FormEditor';

export default function CarePublicSign() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/public/sign/${token}`).then((r) => {
    setD(r.data);
    const next = Object.keys(r.data.forms).find((k) => !['Completed', 'Approved'].includes(r.data.forms[k].status));
    setActive((a) => (a && r.data.forms[a] && !['Completed', 'Approved'].includes(r.data.forms[a].status) ? a : next || null));
  }).catch((e) => setErr(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-slate-600" data-testid="sign-error">{err}</div>;
  if (!d) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-700" /></div>;
  const f = active && d.forms[active];
  const tpl = active && d.templates[active];
  const setForm = (patch) => setD({ ...d, forms: { ...d.forms, [active]: { ...f, ...patch } } });
  const allDone = Object.values(d.forms).every((x) => ['Completed', 'Approved'].includes(x.status));

  const submit = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/public/sign/${token}/${active}`, { data: f.data, signatures: f.signatures });
      toast.success(`${d.labels[active]} signed. Thank you.`);
      window.scrollTo(0, 0);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="care-public-sign">
      <div className="max-w-4xl mx-auto">
        <p className="text-center text-2xl font-extrabold text-[#181B30]">{d.company.company_name}</p>
        <p className="text-center text-sm text-slate-600 mt-1">Documents for <b>{d.resident.name}</b> ({d.resident.resident_id})</p>
        <div className="flex flex-wrap justify-center gap-2 mt-5" data-testid="sign-form-list">
          {Object.entries(d.forms).map(([k, v]) => {
            const done = ['Completed', 'Approved'].includes(v.status);
            return (
              <button key={k} onClick={() => !done && setActive(k)} data-testid={`sign-tab-${k}`}
                className={`px-3.5 py-2 rounded-full text-sm border inline-flex items-center gap-1.5 ${active === k ? 'bg-teal-700 text-white border-teal-700' : done ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-slate-700 border-slate-300'}`}>
                {done ? <CheckCircle2 className="w-4 h-4" /> : <FileSignature className="w-4 h-4" />}{d.labels[k]}
              </button>
            );
          })}
        </div>
        {allDone && (
          <div className="mt-8 bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="sign-all-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="mt-3 font-semibold text-slate-900">All documents have been signed. Thank you!</p>
            <p className="text-sm text-slate-500 mt-1">The care home has received your documents. You can download a copy for your records.</p>
            <a href={`${API}/public/sign/${token}/pdf`} download data-testid="sign-download-pdf"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 text-white text-sm font-semibold hover:bg-teal-800 transition-colors">
              <Download className="w-4 h-4" />Download signed documents (PDF)
            </a>
          </div>
        )}
        {f && (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-8" data-testid={`sign-form-${active}`}>
            <div className="text-center mb-6">
              {active !== 'pdpa' && <p className="font-bold text-slate-800">{tpl.title}</p>}
              <p className="text-xs text-slate-500 mt-1">{d.company.address}</p>
            </div>
            {active === 'admission' && <Admission tpl={tpl} data={f.data} setData={(data) => setForm({ data })} locked={false} lockOffice />}
            {active === 'pdpa' && <Pdpa tpl={tpl} />}
            {active === 'checklist' && <Checklist tpl={tpl} data={f.data || {}} setData={(data) => setForm({ data })} locked={false} />}
            {active === 'contract' && <Contract tpl={tpl} data={f.data} setData={(data) => setForm({ data })} locked={false} co={d.company} />}
            <div className="grid md:grid-cols-2 gap-4 mt-6">
              {tpl.signatures.map((s) => <SigBlock key={s.key} def={s} ft={active} v={f.signatures[s.key]} set={(v) => setForm({ signatures: { ...f.signatures, [s.key]: v } })} locked={false} />)}
            </div>
            {active === 'pdpa' && <p className="text-[11px] text-slate-400 mt-3">{tpl.version}</p>}
            <p className="text-xs text-slate-500 mt-5">By signing, you confirm you have read the document above. Your signature, name and the date/time are recorded.</p>
            <Btn className="mt-4 w-full sm:w-auto" loading={busy} onClick={submit} data-testid="sign-submit">Sign and submit</Btn>
          </div>
        )}
        <p className="text-center text-[11px] text-slate-400 mt-6">Link valid until {d.expires_at.slice(0, 10)} · Powered by StayMii Care</p>
      </div>
    </div>
  );
}
